import React, { useContext, useState, useEffect, useCallback } from 'react';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import UserContext from '../../../Contexts/User';
import { Spinner, ButtonGroup } from 'react-bootstrap';
import Button from '../../Atoms/Button/Button';
import moment from 'moment';
import { scrollToCenter, scrollToTop } from 'common/utils/helper';

// Import types
import type { VoteItem, Pagination, VoteHistoryResponse, Matchup } from './types';

// Import styled components
import {
  Container,
  Title,
  VotesList,
  VoteCard,
  VoteMediaContainer,
  VoteImage,
  VoteVideo,
  PlayIconOverlay,
  VoteContent,
  VoteHeadline,
  VoteCategory,
  VoteInfo,
  VoteRow,
  VoteLabel,
  VoteValue,
  VoteChoice,
  VoteDate,
  ModalOverlay,
  ModalContent,
  CloseButton,
  MediaSection,
  MediaImage,
  MediaVideo,
  MediaOverlay,
  ContentSection,
  ModalInfo,
  PaginationContainer,
  PageInfo,
  LoadingContainer,
  EmptyState,
  TabsContainer,
  TabButton,
} from './styles';

// Firebase functions
const getDailyMatchupVoteHistory = httpsCallable<
  {
    userId: string;
    pageSize: number;
    pageNumber: number;
  },
  VoteHistoryResponse
>(functions, 'getDailyMatchupVoteHistory');

const getPredictionMatchupVoteHistory = httpsCallable<
  {
    userId: string;
    pageSize: number;
    pageNumber: number;
  },
  VoteHistoryResponse
>(functions, 'getPredictionMatchupVoteHistory');

const NewVoteHistory = () => {
  const { user, userInfo } = useContext(UserContext);
  const [votes, setVotes] = useState<VoteItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10,
    totalCount: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedVote, setSelectedVote] = useState<VoteItem | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Tab state
  const [activeTab, setActiveTab] = useState<'daily' | 'prediction'>('daily');

  // Fetch vote history
  const fetchVoteHistory = useCallback(
    async (page: number) => {
      if (!user?.uid) return;

      setIsLoading(true);
      try {
        // Use appropriate function based on active tab
        const fetchFunction = activeTab === 'daily'
          ? getDailyMatchupVoteHistory
          : getPredictionMatchupVoteHistory;

        const result = await fetchFunction({
          userId: user.uid,
          pageSize: 9,
          pageNumber: page,
        });

        const data = result.data;
        if (data.success) {
          setVotes(data.votes || []);
          setPagination(data.pagination);
        }
      } catch (error) {
        console.error('Error fetching vote history:', error);
        setVotes([]);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.uid, activeTab]
  );

  // Load initial data
  useEffect(() => {
    if (user?.uid) {
      // Reset to page 1 when switching tabs
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchVoteHistory(1);
    }
  }, [user?.uid, activeTab, fetchVoteHistory]);

  // Handle page navigation
  const handlePrevPage = () => {
    if (pagination.hasPreviousPage) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
      scrollToTop()
    }

  };

  const handleNextPage = () => {
    if (pagination.hasNextPage) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
      scrollToTop()
    }
  };



  // Handle card click to open modal
  const handleCardClick = (voteItem: VoteItem) => {
    // Save current scroll position
    setScrollPosition(window.scrollY || window.pageYOffset);
    setSelectedVote(voteItem);
    setShowModal(true);
    scrollToCenter();
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVote(null);
    // Restore scroll position after a short delay to ensure modal is closed
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }, 10);
  };

  // Get vote emoji
  const getVoteEmoji = (vote: string) => {
    if (vote === 'up') return '👍';
    if (vote === 'down') return '👎';
    return '😐';
  };

  // Get vote text
  const getVoteText = (vote: string, matchup: Matchup) => {
    if (vote === 'up') return matchup.thumbs_meaning?.up || 'Yes';
    if (vote === 'down') return matchup.thumbs_meaning?.down || 'No';
    return matchup.thumbs_meaning?.neutral || 'Unsure';
  };

  // Check if content is video
  const isVideo = (matchup: Matchup) => {
    return matchup.content_type === 'video' || matchup.mediaSrc;
  };

  // Get media source
  const getMediaSrc = (matchup: Matchup) => {
    return matchup.mediaSrc || matchup.imageUrl;
  };

  // Render vote card
  const renderVoteCard = useCallback((voteItem: VoteItem, index: number) => {
    const { matchup, vote, votedAt } = voteItem;
    const voteDate = moment(votedAt._seconds * 1000).format('MMM DD, YYYY HH:mm');
    const isVideoContent = isVideo(matchup);
    const mediaSrc = getMediaSrc(matchup);

    return (
      <VoteCard key={voteItem.id || index} onClick={() => handleCardClick(voteItem)}>
        <VoteMediaContainer>
          {isVideoContent ? (
            <>
              <VoteVideo
                src={mediaSrc}
                poster={matchup.imageUrl}
                onError={(e) => {
                  (e.target as HTMLVideoElement).poster = `${process.env.PUBLIC_URL}/images/no_logo.png`;
                }}
              />
              <PlayIconOverlay />
            </>
          ) : (
            <VoteImage
              src={matchup.imageUrl}
              alt={matchup.alt_text || matchup.headline}
              onError={(e) => {
                (e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/images/no_logo.png`;
              }}
            />
          )}
        </VoteMediaContainer>
        <VoteContent>
          <VoteCategory>{matchup.category}</VoteCategory>
          <VoteHeadline>{matchup.headline}</VoteHeadline>

          <VoteInfo>
            <VoteRow>
              <VoteLabel>Your Vote:</VoteLabel>
              <VoteChoice $vote={vote}>
                {getVoteEmoji(vote)} {getVoteText(vote, matchup)}
              </VoteChoice>
            </VoteRow>

            <VoteRow>
              <VoteLabel>Total Votes:</VoteLabel>
              <VoteValue>{matchup.votes?.total || 0}</VoteValue>
            </VoteRow>

            {/* <VoteRow>
              <VoteLabel>Status:</VoteLabel>
              <VoteValue $color="#6352e8">
                {matchup.status === 'active' ? '🟢 Active' : '⚪ Inactive'}
              </VoteValue>
            </VoteRow> */}
          </VoteInfo>

          <VoteDate>Voted on {voteDate}</VoteDate>
        </VoteContent>
      </VoteCard>
    );
  }, []);

  return (
    <Container>
      <Title className='pt-4'>Your Vote History</Title>

      {/* Tabs */}
      <TabsContainer>
        <TabButton
          $active={activeTab === 'daily'}
          onClick={() => setActiveTab('daily')}
        >
          Daily Votes
        </TabButton>
        <TabButton
          $active={activeTab === 'prediction'}
          onClick={() => setActiveTab('prediction')}
        >
          Prediction Votes
        </TabButton>
      </TabsContainer>

      {isLoading ? (
        <LoadingContainer>
          <Spinner animation="border" style={{ color: '#ffffff' }} />
        </LoadingContainer>
      ) : votes.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗳️</div>
          No vote history found. Start voting to see your history here!
        </EmptyState>
      ) : (
        <>
          <VotesList>
            {votes.map((vote, index) => renderVoteCard(vote, index))}
          </VotesList>

          <PaginationContainer className='pb-5'>
            <ButtonGroup>
              <Button
                disabled={!pagination.hasPreviousPage || isLoading}
                onClick={handlePrevPage}
              >
                Prev
              </Button>
              <Button
                disabled={!pagination.hasNextPage || isLoading}
                onClick={handleNextPage}
              >
                Next
              </Button>
            </ButtonGroup>
            {/* <PageInfo>
              Page {pagination.currentPage} of {pagination.totalPages}
              {pagination.totalCount > 0 && ` • ${pagination.totalCount} total votes`}
            </PageInfo> */}
          </PaginationContainer>
        </>
      )}

      {/* Modal for viewing full image/video - matching VoteDetailModal */}
      <ModalOverlay $show={showModal} onClick={handleCloseModal}>
        <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <CloseButton onClick={handleCloseModal}>×</CloseButton>

          {selectedVote && (
            <>
              <MediaSection>
                {isVideo(selectedVote.matchup) ? (
                  <MediaVideo
                    src={getMediaSrc(selectedVote.matchup)}
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                    onError={(e: React.SyntheticEvent<HTMLVideoElement>) => {
                      console.error('Video load error');
                    }}
                  />
                ) : (
                  <MediaImage
                    src={selectedVote.matchup.imageUrl}
                    alt={selectedVote.matchup.alt_text || selectedVote.matchup.headline}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      (e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/images/no_logo.png`;
                    }}
                  />
                )}
              </MediaSection>

              <MediaOverlay />

              <ContentSection>
                <ModalInfo>
                  {selectedVote.matchup.headline}
                </ModalInfo>
              </ContentSection>
            </>
          )}
        </ModalContent>
      </ModalOverlay>
    </Container>
  );
};

export default NewVoteHistory;